import React, {
	Suspense,
	useMemo,
	useState,
	useEffect,
	useCallback
} from "react";
import {
	FormattedRelativeTime,
	FormattedDate,
	FormattedNumber,
	IntlProvider
} from "react-intl";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import {
	Cells,
	ExpandableTable,
	ExpandedRow,
	ExpandedRowProps,
	ColumnDefinition
} from "@ploy-ui/core";
import {
	LinearProgress,
	Box,
	Typography,
	Paper,
	TablePagination
} from "@material-ui/core";
import { PendingButton } from "@ploy-ui/core";
import ApplicationActions from "./ApplicationActions";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import {
	ApplicationInfoAdditionalExternalResource,
	ApplicationInfoResource,
	ApplicationInfoDetailsResource,
	ApplicationInfoFilter,
	ApplicationInfo
} from "@ploy-lib/rest-resources";
import { useResource, useFetcher, useInvalidator } from "@rest-hooks/core";
import { useNavigate } from "react-router-dom";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { useFormikContext } from "formik";
import { useDebounce } from "use-debounce";
import { useTenant } from "@ploy-ui/tenants";

const defaultColumnData: ColumnDefinition<
	ApplicationInfoResource,
	ApplicationInfoDetailsResource
>[] = [
	{
		id: "applicationStatusNameExternal",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.status.label"
				description="result table column label for status"
				defaultMessage="Status"
			/>
		),
		component: props => (
			<Cells.ColoredStatus {...props} statusKey="applicationStatusId" />
		),
		hidden: { xsDown: true },
		hiddenInExpandable: { smUp: true }
	},
	{
		id: "applicationNumber",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.applicationNumber.label"
				description="result table column label for applicationNumber"
				defaultMessage="App. nr."
			/>
		),
		hidden: { smDown: true },
		hiddenInExpandable: true
	},
	{
		id: "customerName",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.customerName.label"
				description="result table column label for customer name"
				defaultMessage="Customer"
			/>
		),
		component: props => (
			<Cells.DoubleLineSmDown
				{...props}
				statusKey="applicationStatusId"
				secondLineKey="applicationNumber"
			/>
		),
		hiddenInExpandable: true
	},
	{
		id: "field5",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.object.label"
				description="result table column label for object"
				defaultMessage="Class"
			/>
		),
		hidden: true
	},
	{
		id: "productNameExternal",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.productName.label"
				description="result table column label for product name"
				defaultMessage="Product"
			/>
		),
		hidden: { smDown: true },
		hiddenInExpandable: { mdUp: true }
	},
	{
		id: "startDate",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.startDate.label"
				description="start date of leasing"
				defaultMessage="Start date"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "endDate",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.endDate.label"
				description="end date of leasing"
				defaultMessage="End date"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "field8",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.regNumber.label"
				description="result table column label for object registration number"
				defaultMessage="Registration number"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "field17",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.vinNumber.label"
				description="result table column label for object vin number"
				defaultMessage="Vin number"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "field18",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.prodNumber.label"
				description="result table column label for object production number"
				defaultMessage="Production number"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "internalUser",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.internaluser.label"
				description="result table column label for internal user name"
				defaultMessage="Internal responsible"
			/>
		),
		hidden: true,
		hiddenInExpandable: true,
		expandedComponent: ({ rowData, loadedData }) => {
			return (
				<>
					<span>{rowData.internalUser}</span>
					<br />
					<span>
						<a
							href={
								"mailto:" +
								loadedData?.responsible.email +
								"?subject=" +
								rowData.applicationNumber
							}
						>
							{loadedData?.responsible.email}
						</a>
					</span>
					{loadedData?.responsible.mobile && (
						<>
							<br />
							<span>Tlf: {loadedData?.responsible.mobile}</span>
						</>
					)}
				</>
			);
		}
	},
	{
		id: "backofficeAccountId",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.accountId.label"
				description="result table column label for backoffice account id"
				defaultMessage="Agreement number"
			/>
		),
		hidden: true,
		hiddenInExpandable: (_, { productCodeExternal }) =>
			productCodeExternal !== "LEIE"
	},
	{
		id: "salesPersonName",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.salesPersonName.label"
				description="result table column label for salesPersonName"
				defaultMessage="Salesperson"
			/>
		),
		hidden: { smDown: true },
		hiddenInExpandable: { mdUp: true }
	},
	{
		id: "documentStatusNameExternal",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.documentStatusName.label"
				description="result table column label for document status name external"
				defaultMessage="Doc. status"
			/>
		),
		hiddenInExpandable: true
	},
	{
		id: "createdDate",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.createdDate.label"
				description="result table column label for created date"
				defaultMessage="Delivery date"
			/>
		),
		hidden: true,
		format: val =>
			val && (
				<FormattedDate value={val} day="numeric" month="long" year="numeric" />
			)
	},
	{
		id: "paidDate",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.paidDate.label"
				description="result table column label for paid date"
				defaultMessage="Payment date"
			/>
		),
		hidden: true,
		hiddenInExpandable: val => !val,
		format: val =>
			val && (
				<FormattedDate value={val} day="numeric" month="long" year="numeric" />
			)
	},
	{
		id: "applicationLastChanged",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.applicationLastChanged.label"
				description="result table column label for application last changed"
				defaultMessage="Last change"
			/>
		),
		hidden: true,
		tooltip: val =>
			val && (
				<FormattedDate
					value={val}
					day="numeric"
					month="long"
					year="numeric"
					hour="numeric"
					minute="numeric"
				/>
			),
		format: val =>
			val && (
				<FormattedMessage
					id="dealer.allapplications.result.column.applicationLastChanged.value"
					defaultMessage={"{relative}"}
					values={{
						relative: (
							<FormattedRelativeTime
								value={(new Date(val).getTime() - new Date().getTime()) / 1000}
								unit="second"
								updateIntervalInSeconds={10}
							/>
						),
						date: (
							<FormattedDate
								value={val}
								day="numeric"
								month="long"
								year="numeric"
								hour="numeric"
								minute="numeric"
							/>
						)
					}}
				/>
			)
	},
	{
		id: "loanAmount",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.loanAmount.label"
				description="result table column label for loan amount"
				defaultMessage="Loan amount"
			/>
		),
		hidden: true,
		format: val => val && <FormattedNumber value={val} format="currency" />
	},
	{
		loadedId: "responsible.mobile",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.responsible.mobile.label"
				description="result table column label for responsible mobile"
				defaultMessage="Phone coordinator"
			/>
		),
		hidden: true,
		hiddenInExpandable: (_, __, details) =>
			details?.responsible.mobile !== "undefined"
	},
	{
		loadedId: "responsible.email",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.responsible.email.label"
				description="result table column label for responsible email"
				defaultMessage="Email coordinator"
			/>
		),
		hidden: true,
		component: props => (
			<Cells.MailtoLink {...props} subject={row => row.applicationNumber} />
		),
		hiddenInExpandable: (_, __, details) =>
			details?.responsible.email !== "undefined"
	},
	{
		id: "actions",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.actions.label"
				description="result table column label for actions"
				defaultMessage="Actions"
			/>
		),
		hidden: { mdDown: true },
		hiddenInExpandable: { mdUp: true },
		component: props => <ApplicationActions {...props} />
	},
	{
		id: "invoiceNoRef",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.invoiceNoRef.label"
				description="reference number for application"
				defaultMessage="Reference Number"
			/>
		),
		hidden: true,
		hiddenInExpandable: true
	},
	{
		id: "termAmount",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.termAmount.label"
				description="payment amount per term"
				defaultMessage="Term amount"
			/>
		),
		hidden: true,
		hiddenInExpandable: true,
		expandedComponent: ({ rowData, loadedData }) => {
			return (
				<>
					{loadedData?.termAmount && (
						<IntlProvider locale="no-NO">
							<FormattedNumber
								value={loadedData.termAmount}
								minimumFractionDigits={2}
								maximumFractionDigits={2}
							>
								{(formatted: string) =>
									formatted.endsWith(",00")
										? formatted.replace(",00", ",-")
										: formatted
								}
							</FormattedNumber>
						</IntlProvider>
					)}
				</>
			);
		}
	},
	{
		id: "totalTerms",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.totalTerms.label"
				description="total number of terms of application"
				defaultMessage="Number of terms"
			/>
		),
		hidden: true,
		hiddenInExpandable: true,
		expandedComponent: ({ rowData, loadedData }) => {
			return (
				<>
					{loadedData?.totalTerms && (
						<FormattedMessage
							id="dealer.allapplications.leasingTermsLeft"
							description="Number of terms left"
							defaultMessage="{term}.terms"
							values={{
								term: loadedData.totalTerms
							}}
						/>
					)}
				</>
			);
		}
	},
	{
		id: "paymentInterval",
		label: (
			<FormattedMessage
				id="dealer.allapplications.result.column.paymentInterval.label"
				description="payment interval for application"
				defaultMessage="Payment interval"
			/>
		),
		hidden: true,
		hiddenInExpandable: true,
		expandedComponent: ({ rowData, loadedData }) => {
			const paymentIntervals = {
				1: "monthly",
				3: "quarterly",
				6: "semiannually",
				12: "annually"
			};

			const interval = loadedData?.paymentInterval;
			const intervalKey = interval as keyof typeof paymentIntervals;

			return interval && paymentIntervals[intervalKey] ? (
				<FormattedMessage
					id={`dealer.allapplications.paymentInterval.${paymentIntervals[intervalKey]}`}
					description="payment interval"
					defaultMessage={
						paymentIntervals[intervalKey]?.charAt(0).toUpperCase() +
						paymentIntervals[intervalKey].slice(1)
					}
				/>
			) : null;
		}
	}
];

export const messages = defineMessages({
	lblCancelWarningApplication: {
		id: "dealer.allapplications.searchresults.cancelWarningApplication",
		description: "Label for cancel application alert",
		defaultMessage: "Are you sure you want to cancel case: {applicationNumber}"
	}
});

const useStyles = makeStyles(theme => ({
	applicationStatusNameExternal: {
		width: 160,
		textAlign: "center"
	},
	applicationNumber: {
		width: 100
	},
	customerNameInner: {
		display: "flex",
		alignItems: "center",
		"& > div": {
			marginRight: 10
		}
	},
	customerNameText: {
		fontWeight: "bold"
	},
	cancelApplication: {
		float: "right"
	},
	actions: {
		padding: 0
	},
	noResultPaper: {
		width: "100%",
		padding: "18.5px 14px",
		marginBottom: "10%"
	},
	noResultText: {
		textAlign: "center",
		fontWeight: 700
	},
	cellPadding: {
		padding: `${theme.spacing(2)}px ${theme.spacing(0.5)}px`
	},
	pagination: {
		backgroundColor: "#eeeeee"
	}
}));

const normalizeAdditionalData = (
	payload: unknown
): ApplicationInfoAdditionalExternalResource[] => {
	if (Array.isArray(payload)) {
		return payload as ApplicationInfoAdditionalExternalResource[];
	}

	if (
		payload &&
		typeof payload === "object" &&
		Array.isArray(
			(payload as { data?: ApplicationInfoAdditionalExternalResource[] }).data
		)
	) {
		return (payload as { data: ApplicationInfoAdditionalExternalResource[] })
			.data;
	}

	return [];
};

const SearchResults = ({ columnData = defaultColumnData }) => {
	const classes = useStyles();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	// Sorry for this, big problems when sorting data and changing page, causing reloads,
	// this is a very hacky solution but it is what it is.
	const [sortablePage, setSortablePage] = useState(0);
	const [sortableRowsPerPage, setSortableRowsPerPage] = useState(20);
	const tenantConfig = useTenant();
	const columnSettings = tenantConfig?.tenant?.settings?.allApplicationsColumns;
	const shouldFetchAdditionalData =
		tenantConfig?.tenant?.settings?.getAdditionalDataForAllApplications;

	const tableIsSortable =
		tenantConfig.tenant?.settings?.tableIsSortable ?? false;

	const { values } = useFormikContext<ApplicationInfoFilter>();

	const [searchParams] = useDebounce(values, 200, { leading: true });

	/*The reason for the tableIsSortable shenanigans below is that if we have
	the table sortable, we want that sorting to be done on all the data in
	the table, not just the current page. Therefore we need to make sure
	that we get all possible filtered applications and that they then get
	sorted internally in expandabletable.*/
	const params = useMemo(
		() => ({
			...searchParams,
			page: tableIsSortable ? 1 : page + 1,
			pageSize: tableIsSortable ? 50000 : rowsPerPage,
			mustNotHaveHadAppStatus:
				tenantConfig?.tenant?.settings?.mustNotHaveHadAppStatus
		}),
		[page, rowsPerPage, searchParams, tableIsSortable]
	);

	const {
		records,
		recordCount,
		pageSize = rowsPerPage
	} = useResource(ApplicationInfoResource.list(), params);

	const [additionalData, setadditionalData] = useState<
		ApplicationInfoAdditionalExternalResource[]
	>([]);
	const fetchAdditionalData = useFetcher(
		ApplicationInfoAdditionalExternalResource.list()
	);

	useEffect(() => {
		if (!shouldFetchAdditionalData || additionalData.length > 0) return;

		fetchAdditionalData({}, undefined).then(data => {
			setadditionalData(normalizeAdditionalData(data));
		});
	}, [shouldFetchAdditionalData, additionalData.length, fetchAdditionalData]);

	const mergedRecords = useMemo(() => {
		if (!additionalData.length) return records;

		const additionalDataMap = new Map(
			additionalData.map(d => [d.accountNumber, d])
		);
		return records.map((record: ApplicationInfoResource) => {
			const match = additionalDataMap.get(record.applicationNumber);

			return {
				...record,
				startDate: match?.startDate
					? new Date(match.startDate).toLocaleDateString("no-NO", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit"
					  })
					: null,
				endDate: match?.endDate
					? new Date(match.endDate).toLocaleDateString("no-NO", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit"
					  })
					: null
			};
		});
	}, [records, additionalData]);

	const invalidator = useInvalidator(ApplicationInfoResource.list());

	useEffect(
		() => () => {
			invalidator(params);
		},
		[invalidator, params]
	);

	const individualAppInvalidator = useInvalidator(
		ApplicationInfoResource.detail()
	);
	const individualAppFetcher = useFetcher(ApplicationInfoResource.detail());

	const individualAppDetailInvalidator = useInvalidator(
		ApplicationInfoDetailsResource.detail()
	);
	const individualAppDetailsFetcher = useFetcher(
		ApplicationInfoDetailsResource.detail()
	);

	const reloadApp = useCallback(
		(app: ApplicationInfo) => {
			individualAppInvalidator({ applicationNumber: app.applicationNumber });
			individualAppDetailInvalidator({ appNr: app.applicationNumber });
			individualAppFetcher(
				{ applicationNumber: app.applicationNumber },
				undefined
			);
			individualAppDetailsFetcher({ appNr: app.applicationNumber }, undefined);
		},
		[
			individualAppDetailInvalidator,
			individualAppDetailsFetcher,
			individualAppFetcher,
			individualAppInvalidator
		]
	);

	const handleChangePage = (_: unknown, newPage: number) => {
		if (!tableIsSortable) setPage(newPage);
		setSortablePage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (!tableIsSortable) {
			setRowsPerPage(parseInt(event.target.value, 10));
			setPage(0);
		}
		setSortableRowsPerPage(parseInt(event.target.value, 10));
		setSortablePage(0);
	};

	const columns = useMemo(
		() =>
			columnData.map(c => {
				let { id, hidden, hiddenInExpandable, sortOrder } = c;

				let colSetting = columnSettings?.find(x => x.id === id);

				let reloadData: ((row: ApplicationInfo) => void) | undefined =
					undefined;

				if (colSetting) {
					hidden = colSetting.hidden ?? hidden;
					hiddenInExpandable =
						colSetting.hiddenInExpandable ?? hiddenInExpandable;
					sortOrder = colSetting.sortOrder;
				}

				const hasField8Values = mergedRecords.some(
					(record: ApplicationInfoResource) => record.field8
				);
				const hasField17Values = mergedRecords.some(
					(record: ApplicationInfoResource) => record.field17
				);

				const showAll =
					searchParams.salepersonId === undefined ||
					searchParams.salepersonId === "";

				switch (c.id) {
					case "actions":
						if (
							showAll &&
							(tenantConfig.tenant?.settings?.showActions ?? true)
						) {
							hidden = true;
							hiddenInExpandable = undefined;
						}
						reloadData = reloadApp;
						break;
					case "salesPersonName":
						if (
							!showAll &&
							(tenantConfig.tenant?.settings?.showSalesPersonName ?? true)
						) {
							hidden = true;
							hiddenInExpandable = undefined;
						}
						break;
					case "internalUser":
						if (
							showAll &&
							(tenantConfig.tenant?.settings?.showInternalUser ?? true)
						) {
							hidden = true;
							hiddenInExpandable = undefined;
						}
						break;
					case "field8":
						if (
							!hasField8Values &&
							tenantConfig.tenant?.settings?.hideField8Column
						) {
							hidden = true;
							hiddenInExpandable = undefined;
						}
						if (
							tenantConfig.tenant?.settings?.hideField8AndField17FromExpandable
						) {
							hiddenInExpandable = true;
						}
						break;
					case "field17":
						if (
							!hasField17Values &&
							tenantConfig.tenant?.settings?.hideField17Column
						) {
							hidden = true;
							hiddenInExpandable = undefined;
						}
						if (
							tenantConfig.tenant?.settings?.hideField8AndField17FromExpandable
						) {
							hiddenInExpandable = true;
						}
						break;
					default:
						break;
				}

				return {
					...c,
					className: clsx(
						classes[c.id as keyof typeof classes],
						classes.cellPadding
					),
					hidden,
					hiddenInExpandable,
					sortOrder,
					props: reloadData != null ? { ...c.props, reloadData } : c.props
				};
			}),
		[
			columnData,
			columnSettings,
			mergedRecords,
			searchParams.salepersonId,
			classes,
			reloadApp,
			tenantConfig.tenant?.settings?.hideField8Column,
			tenantConfig.tenant?.settings?.hideField17Column
		]
	);

	const navigate = useNavigate();

	return recordCount > 0 ? (
		<>
			<ExpandableTable
				tableType="applicationListContainer"
				idColumn="applicationId"
				tableData={mergedRecords}
				columnData={columns}
				onRowClick={row => navigate(row.applicationNumber)}
				renderExpandedSection={row => {
					return (
						<Suspense fallback={<LinearProgress />}>
							<SearchResultExpandedRow
								rowData={row}
								columnData={columns}
								searchParams={params}
							/>
						</Suspense>
					);
				}}
				tableIsSortable={tableIsSortable}
				{...(tableIsSortable && {
					pagination: { page: sortablePage, rowsPerPage: sortableRowsPerPage }
				})}
			/>
			<TablePagination
				className={classes.pagination}
				rowsPerPageOptions={[10, 20, 50]}
				component="div"
				count={recordCount}
				rowsPerPage={tableIsSortable ? sortableRowsPerPage : pageSize}
				page={tableIsSortable ? sortablePage : page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	) : (
		<Paper className={classes.noResultPaper}>
			<Typography className={classes.noResultText}>
				<FormattedMessage
					id="dealer.allapplications.searchresults.noresult"
					description="Label for no result from searchresult"
					defaultMessage="No results"
				/>
			</Typography>
		</Paper>
	);
};

function SearchResultExpandedRow(
	props: {
		searchParams: {};
	} & ExpandedRowProps<ApplicationInfoResource, ApplicationInfoDetailsResource>
) {
	const { rowData, searchParams } = props;

	const loadedData = useResource(ApplicationInfoDetailsResource.detail(), {
		appNr: rowData.applicationNumber
	});

	const refresh = useFetcher(ApplicationInfoResource.list());
	const refreshDetails = useFetcher(ApplicationInfoDetailsResource.detail());

	return (
		<>
			<ExpandedRow loadedData={loadedData} {...props} />

			{loadedData.canCancelApp && (
				<CancelButton
					applicationId={rowData.applicationId}
					applicationNumber={rowData.applicationNumber}
					onSuccess={() => {
						refresh(searchParams);
						refreshDetails({
							appNr: rowData.applicationNumber
						});
					}}
				/>
			)}
		</>
	);
}

function CancelButton(
	props: Pick<
		ApplicationInfoResource,
		"applicationId" | "applicationNumber"
	> & { onSuccess?: () => void }
) {
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState<unknown>();

	const { applicationId, applicationNumber, onSuccess } = props;

	const intl = useIntl();

	const cancel = async () => {
		setLoading(true);

		const payload = {
			applicationId
		};

		try {
			if (
				window.confirm(
					intl.formatMessage(messages.lblCancelWarningApplication, {
						applicationNumber
					})
				)
			) {
				const res = await fetch(
					legacyApiResourceUrl("AllApplications/CancelApplication"),
					{
						method: "POST",
						body: new Blob([JSON.stringify(payload)], {
							type: "application/json"
						}),
						headers: {
							accept: "application/json"
						}
					}
				);

				if (!res.ok) {
					throw new Error("Failed to cancel application");
				}

				setSuccess(true);

				if (onSuccess) onSuccess();
			}
		} catch (error) {
			setError(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box display="flex" justifyContent="flex-end">
			{error instanceof Error ? (
				<Typography color="error">{error.message}</Typography>
			) : null}
			<PendingButton
				color="secondary"
				variant="contained"
				onClick={cancel}
				pending={loading}
				success={success}
				error={!!error}
			>
				<FormattedMessage
					id="dealer.allapplications.searchresults.cancelapplication"
					description="Label for cancel application button"
					defaultMessage="Cancel application"
				/>
			</PendingButton>
		</Box>
	);
}

export default SearchResults;
